"use client";

import { selectFilterOptions } from "@/feature/flight/search/state/selectors";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { Moon, Sun, Sunrise, Sunset } from "lucide-react";
import { motion } from "motion/react";
import { memo, useState } from "react";
import {
  setFilters,
} from "@/feature/flight/search/state/slices/extract-flight-filter.slice";
import AccordionSection from "./accordion-section";

const type = ["departure", "arrival"];

const iconComponents = [
  (isSelected: boolean) => (
    <Sunrise
      className={`w-4 h-4 transition-all ${
        isSelected ? "text-primary scale-110" : "text-muted-foreground"
      }`}
      strokeWidth={1.5}
    />
  ),
  (isSelected: boolean) => (
    <Sun
      className={`w-4 h-4 transition-all ${
        isSelected ? "text-primary scale-110" : "text-muted-foreground"
      }`}
      strokeWidth={1.5}
    />
  ),
  (isSelected: boolean) => (
    <Sunset
      className={`w-4 h-4 transition-all ${
        isSelected ? "text-primary scale-110" : "text-muted-foreground"
      }`}
      strokeWidth={1.5}
    />
  ),
  (isSelected: boolean) => (
    <Moon
      className={`w-4 h-4 transition-all ${
        isSelected ? "text-primary scale-110" : "text-muted-foreground"
      }`}
      strokeWidth={1.5}
    />
  ),
];

const timeLabels = ["Morning", "Noon", "Evening", "Night"];

const FlightSchedules = memo(() => {
  const dispatch = useAppDispatch();
  const { selectOption, defaultOption } = useAppSelector(
    selectFilterOptions,
  );
  const isResponseEnd = useAppSelector((state) => state.flightSearchStatus.isResponseEnd);

  const [select, setSelect] = useState<"departure" | "arrival">("departure");

  const timeFrames = selectOption?.timeFrame ?? [];
  const data = defaultOption?.time_frame;
  const selectedData = select === "departure" ? data?.departure : data?.arrival;

  const handleTimeClick = (code: string, time: string, arr_index: number) => {
    const newData = { type: select, code, time, arr_index };

    const filteredTimeFrames = timeFrames.filter(
      (frame) =>
        !(frame.type === newData.type && frame.arr_index === newData.arr_index),
    );

    const exists = timeFrames.some(
      (frame) =>
        frame.type === newData.type &&
        frame.code === newData.code &&
        frame.time === newData.time &&
        frame.arr_index === newData.arr_index,
    );

    const updatedTimeFrames = exists
      ? filteredTimeFrames
      : [...filteredTimeFrames, newData];

    dispatch(
      setFilters({
        timeFrame: updatedTimeFrames.length ? updatedTimeFrames : undefined,
      }),
    );
  };

  return (
    <AccordionSection value="flight_schedules" label="Flight Schedules">
      <div className="space-y-3">
        <div className="relative mx-auto flex h-8 w-full overflow-hidden rounded-md border p-0.5">
          <div
            className="absolute inset-0 z-0 flex"
            style={{ pointerEvents: "none" }}
          >
            <motion.div
              className="h-full w-1/2 bg-primary"
              initial={false}
              animate={{
                x: select === "departure" ? 0 : "100%",
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          </div>

          {type.map((item) => (
            <button
              key={item}
              onClick={() =>
                isResponseEnd && setSelect(item as "departure" | "arrival")
              }
              disabled={!isResponseEnd}
              className={`relative z-10 flex-1 text-center rounded-full text-xs font-medium transition-colors duration-200 
              ${
                item === select ? "text-primary-foreground" : "text-foreground"
              } 
              ${!isResponseEnd ? "opacity-50 cursor-not-allowed" : ""}
            `}
            >
              {item.charAt(0).toUpperCase() + item.slice(1)}
            </button>
          ))}
        </div>

        {/* Flight time slots */}
        <div className="space-y-3">
          {selectedData?.length ? (
            selectedData.map(({ code, times }, arr_index) => {
              const timeSlots = times
                .split(";")
                .map((slot) => slot.trim())
                .filter(Boolean);

              return (
                <div key={arr_index} className="bg-muted/30 rounded-lg p-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <h3 className="text-xs font-medium">
                        {select === "arrival" ? "To" : "From"}{" "}
                        <span className="font-bold">{code}</span>
                      </h3>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {select.charAt(0).toUpperCase() + select.slice(1)}
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-1">
                    {timeSlots.map((time, timeIdx) => {
                      const icon =
                        iconComponents[timeIdx] ??
                        iconComponents[iconComponents.length - 1];
                      const timeLabel =
                        timeLabels[timeIdx] ?? `Slot ${timeIdx + 1}`;
                      const isSelected = timeFrames?.some(
                        (frame) =>
                          frame.type === select &&
                          frame.code === code &&
                          frame.time === time &&
                          frame.arr_index === arr_index,
                      );

                      return (
                        <button
                          key={`${code}-${time}-${timeIdx}`}
                          onClick={() =>
                            isResponseEnd &&
                            handleTimeClick(code, time, arr_index)
                          }
                          disabled={!isResponseEnd}
                          className={`group flex flex-col items-center justify-center py-1.5 rounded-md transition-all duration-200
                      ${
                        isSelected
                          ? "bg-primary/10 ring-1 ring-primary/30"
                          : "hover:bg-muted"
                      } 
                      ${
                        !isResponseEnd
                          ? "opacity-40 cursor-not-allowed grayscale"
                          : ""
                      }
                    `}
                        >
                          <div className="mb-0.5">{icon(isSelected)}</div>
                          <p
                            className={`text-[10px] font-medium ${
                              isSelected
                                ? "text-primary"
                                : "text-muted-foreground"
                            }`}
                          >
                            {time}
                          </p>
                          <p
                            className={`text-[10px] ${
                              isSelected
                                ? "text-primary/70"
                                : "text-muted-foreground/70"
                            }`}
                          >
                            {timeLabel}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-muted-foreground">
              Flight schedule options will appear once results are loaded.
            </p>
          )}
        </div>
      </div>
    </AccordionSection>
  );
});

FlightSchedules.displayName = "FlightSchedules";
export default FlightSchedules;


